'use client'

import { useTransition } from 'react'
import { usePathname, useRouter, useSearchParams } from 'next/navigation'

import { LoadingOverlay } from '@/app/components/shared/LoadingOverlay'
import { LocationPicker } from '@/app/components/shared/LocationPicker'
import { setManualLocation } from '@/app/lib/geolocation'
import { invalidateResolvedTargetCache } from '@/app/lib/hooks/useGeoLocationSync'
import { buildLocalizedPath } from '@/app/lib/routing'

import {
  DYNAMIC_TITLE_PREFIX,
  labelForLocation,
  LOCATION_CHANGE_LOADING_LABEL,
  LOCATION_PICKER_ANALYTICS_COMPONENT_ID,
  LOCATION_PICKER_ANALYTICS_COMPONENT_LABEL,
  LOCATION_PICKER_ANALYTICS_MODULE_NAME,
} from './ProductosLocalizados.constants'
import { ProductosLocalizadosLink } from './ProductosLocalizadosLink'
import styles from './ProductosLocalizadosHeader.module.scss'

interface ProductosLocalizadosHeaderProps {
  /** Título del módulo tal como llega del CMS (vacío/solo-espacios ⇒ título
   * dinámico por localización, FR-001/FR-003). */
  readonly title: string | undefined
  readonly headingId: string
  /** Texto del enlace "ver todos" ya resuelto por `ProductosLocalizados.tsx`
   * (fallback fijo o `link_title` del CMS). */
  readonly linkTitle: string
  /** `true` ⇒ `link_title` llegó vacío, o `link_url` llegó vacío/con el
   * placeholder `"#"`: el destino se resuelve aquí, con la localización
   * ACTUAL (no la fija `ALL_LOCATIONS` del `fallback` estático del
   * `Suspense`). `false` ⇒ usar `verbatimLinkHref` tal cual. */
  readonly usesFallbackLink: boolean
  /** Destino "tal cual" del CMS (`link_url`, ya absolutizado) — solo se usa
   * cuando `usesFallbackLink` es `false`; cadena vacía en caso contrario. */
  readonly verbatimLinkHref: string
  readonly openInNewTab: boolean
  /** Localización de la página anfitriona (URL), no de `useCityContext()` —
   * ver `ProductosLocalizados.tsx`/`withLocation`. */
  readonly location: string
}

/**
 * `ProductosLocalizadosHeader` — cabecera completa del módulo: título
 * (dinámico/estático) + `LocationPicker` en un único `<h2>` en línea (paridad
 * con la referencia: `sectionTitle` es un flex en baseline que contiene el
 * texto y el `CityPicker` como último elemento) + enlace "ver todos".
 *
 * `location` llega como prop (la de la URL de la página anfitriona, ver
 * `ProductosLocalizados.tsx`/`withLocation`) — NO de `useCityContext()`: ese
 * contexto global puede quedar desincronizado de la URL real de esta página
 * concreta (cookie de una visita anterior a otra provincia), lo que rompía
 * silenciosamente `buildLocalizedPath` (comparaba contra un segmento que no
 * existía en el `pathname` real y nunca navegaba).
 *
 * Cuando `title` llega vacío, el texto plano es solo el prefijo
 * ("Entradas de lo más vendido en") — la propia localización NUNCA se repite
 * como texto aparte: es el botón del `LocationPicker` el que la muestra y
 * permite cambiarla.
 *
 * El enlace "ver todos" vive en `ProductosLocalizadosLink` (resuelve su
 * propio destino, incluida la rama de fallback con la localización ACTUAL) y
 * se pinta aquí SOLO desde tablet (`styles.link` lo oculta en móvil, ver
 * `ProductosLocalizadosHeader.module.scss`) — en móvil se pinta la otra
 * instancia, debajo del carrusel (`ProductosLocalizados.tsx`), paridad con
 * `eci-firefly-uikit`.
 *
 * Al cambiar de localización: persiste la elección (`setManualLocation`,
 * Server Action) y navega -sin recarga completa y sin perder la posición de
 * scroll (`{ scroll: false }`, para no perder de vista el propio módulo)-
 * cuando la página actual tiene un segmento de localización addressable
 * (`buildLocalizedPath`); si no lo tiene, no navega, solo persiste la cookie
 * (research.md §5). La navegación resultante recarga la página (Server
 * Component) con la nueva localización, así que este módulo — y cualquier
 * `Agenda` en la misma página — quedan re-filtrados por ella.
 *
 * El `router.push` va envuelto en `startTransition` para exponer `isPending`
 * y pilotar el `LoadingOverlay` general del sitio (mismo componente que usa
 * `NavigationProgress`) mientras dura la navegación: el click que la dispara
 * ocurre en un `<button>` (`LocationPicker`), y `NavigationProgress` solo
 * detecta clicks sobre `a[href]`, así que nunca lo mostraría por su cuenta.
 * No se muestra si `buildLocalizedPath` devuelve `null` (no hay navegación,
 * solo persistencia de cookie).
 */
export function ProductosLocalizadosHeader({
  title,
  headingId,
  linkTitle,
  usesFallbackLink,
  verbatimLinkHref,
  openInNewTab,
  location,
}: ProductosLocalizadosHeaderProps) {
  const router = useRouter()
  const pathname = usePathname()
  const searchParams = useSearchParams()
  const [isNavigating, startTransition] = useTransition()

  const trimmedTitle = title?.trim() ?? ''
  const staticTitle = trimmedTitle !== '' ? trimmedTitle : DYNAMIC_TITLE_PREFIX

  async function handleSelect(newLocation: string) {
    // `await` + invalidar la caché: ver `AgendaHeader`.
    await setManualLocation(newLocation)
    invalidateResolvedTargetCache()

    const nextPath = buildLocalizedPath(pathname, location, newLocation)
    if (nextPath === null) return

    const qs = searchParams.toString()
    startTransition(() => {
      // `scroll: false`: mantiene la posición de scroll — el módulo debe
      // seguir en la vista tras el cambio de localización, no saltar al
      // inicio de la página.
      router.push(qs ? `${nextPath}?${qs}` : nextPath, { scroll: false })
    })
  }

  return (
    <>
      {isNavigating && <LoadingOverlay label={LOCATION_CHANGE_LOADING_LABEL} />}
      <div className={styles.header}>
        <h2 id={headingId} className={styles.title}>
          {staticTitle}
          {/* Espacio literal (no solo el `gap` visual del flex): el nombre
              accesible del `<h2>` concatena el texto de sus descendientes
              — sin él, el botón del selector quedaría pegado al texto para
              lectores de pantalla aunque se vea bien visualmente. */}{' '}
          <LocationPicker
            location={location}
            onSelect={handleSelect}
            getLabel={labelForLocation}
            analytics={{
              moduleName: LOCATION_PICKER_ANALYTICS_MODULE_NAME,
              componentId: LOCATION_PICKER_ANALYTICS_COMPONENT_ID,
              componentLabel: LOCATION_PICKER_ANALYTICS_COMPONENT_LABEL,
            }}
          />
        </h2>

        <ProductosLocalizadosLink
          linkTitle={linkTitle}
          location={location}
          usesFallbackLink={usesFallbackLink}
          verbatimLinkHref={verbatimLinkHref}
          openInNewTab={openInNewTab}
          className={styles.link}
        />
      </div>
    </>
  )
}
